import { useGetCollectionsSummaryQuery } from "@/services/dashboard";
import { useState, useEffect } from "react";
import {
  CHART_COLORS,
  CHART_OPTIONS,
  MONTH_NAMES,
  CHART_LABELS,
  MOCK_SCHOOLS,
  YEAR_RANGE,
  School,
} from "./constants";
import { useGetSchoolsQuery } from "@/services";

interface CollectionsSummaryData {
  month: string;
  invoicedAmount: number;
  collectedAmount: number;
}

interface CollectionsSummaryParams {
  year?: number;
  schoolId?: string;
}

const useCollectionsSummary = () => {
  // State management
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [selectedSchool, setSelectedSchool] = useState<string>("");

  const { data, error, isLoading } = useGetCollectionsSummaryQuery({
    year: selectedYear,
    schoolId: selectedSchool || undefined,
  });

  // Generate years for dropdown (current year ± 5 years)
  const generateYears = () => {
    return Array.from({ length: 11 }, (_, i) => {
      const year =
        new Date().getFullYear() - YEAR_RANGE.YEARS_BEFORE_CURRENT + i;
      return { value: year.toString(), label: year.toString() };
    });
  };

  const years = generateYears();
  const { data: schoolRes, isLoading: isLoadingSchool } = useGetSchoolsQuery({});
  
    const schoolOptions =
      schoolRes?.data?.[0]?.records?.map((item: any) => ({
        label: item.schoolName,
        value: item.schoolId,
      })) || [];

  // Function to map month numbers to month names
  const getMonthName = (month: string | number): string => {
    const monthNum = typeof month === "string" ? parseInt(month) : month;
    return MONTH_NAMES[monthNum - 1] || month.toString();
  };

  // Process collections summary data
  const collectionsSummary: CollectionsSummaryData[] = data?.data?.items || [];

  // Generate chart data
  const chartData = {
    labels: collectionsSummary.map((item) => getMonthName(item.month)),
    datasets: [
      {
        label: CHART_LABELS.ACTUAL_COLLECTION,
        data: collectionsSummary.map((item) => item.collectedAmount),
        backgroundColor: CHART_COLORS.ACTUAL_COLLECTION.backgroundColor,
        borderColor: CHART_COLORS.ACTUAL_COLLECTION.borderColor,
        borderWidth: 1,
        borderRadius: 7,
        pointStyle: "rect",
      },
      {
        label: CHART_LABELS.TARGET,
        data: collectionsSummary.map((item) => item.invoicedAmount),
        backgroundColor: CHART_COLORS.TARGET.backgroundColor,
        borderColor: CHART_COLORS.TARGET.borderColor,
        borderWidth: 1,
        borderRadius: 7,
        pointStyle: "rect",
      },
    ],
  };

  return {
    // Data
    collectionsSummary,
    chartData,
    chartOptions: CHART_OPTIONS,

    // State
    selectedYear,
    setSelectedYear,
    selectedSchool,
    setSelectedSchool,
    schoolOptions,
    years,

    // Loading states
    isLoading,
    error,
  };
};

export default useCollectionsSummary;
